/**
 * The item table: one entry per `ItemKind`.
 *
 * Effects are absolute points on the 0..100 stats. Nothing here is a ration —
 * there is no clock to feed. These are the things somebody left behind, and
 * what they are worth is what it is worth to sit down in an empty building and
 * eat a granola bar: not much, and more than nothing. Almond water is the
 * exception and the reason to open the closets.
 */

import type { ItemDef, ItemKind, StatKey, SurvivalStats } from '../core/types';

/**
 * `ItemDef` plus the strings the survival system needs but core does not model:
 * the prompt verb and the lines shown when an item cannot do anything.
 */
export interface ItemDefEx extends ItemDef {
  /** Verb for the world prompt, e.g. 'Take Almond Water'. */
  verb: string;
  /** Shown when the player uses it and every stat it touches is already full. */
  noEffect: string;
  /** Shown when the item cannot be used from the inventory at all. */
  refusal?: string;
}

const DEFS: Record<ItemKind, ItemDefEx> = {
  'almond-water': {
    kind: 'almond-water',
    label: 'Almond Water',
    description:
      'Tastes faintly of almonds. It should not, and it does, and for a while afterwards the rooms are easier to be in.',
    consumable: true,
    stackSize: 4,
    // The prize. Nothing else in the building gives back this much of the part
    // of you that has been counting doors, and nothing else is worth carrying
    // past a room you have not looked in.
    effects: { sanity: 60 },
    color: 0xf3e9c8,
    verb: 'Take',
    noEffect: 'You are steady. Keep it for when you are not.',
  },
  'water-bottle': {
    kind: 'water-bottle',
    label: 'Water Bottle',
    description: "Someone else's bottle, refilled from somewhere and left standing upright. Slightly warm.",
    consumable: true,
    stackSize: 4,
    effects: { sanity: 12 },
    color: 0x9fd8e8,
    verb: 'Take',
    noEffect: 'You do not need it right now.',
  },
  granola: {
    kind: 'granola',
    label: 'Granola Bar',
    description: 'Oats and syrup pressed flat. The wrapper is a brand that does not exist.',
    consumable: true,
    stackSize: 6,
    effects: { sanity: 10 },
    color: 0xb98a4a,
    verb: 'Take',
    noEffect: 'You do not need it right now.',
  },
  'canned-food': {
    kind: 'canned-food',
    label: 'Canned Food',
    description: 'Unlabelled, peel-top, no tool needed. The contents are grey, and warm enough to sit down for.',
    consumable: true,
    stackSize: 4,
    // The only thing besides a bandage that puts health back on the spot, which
    // is what stopping somewhere long enough to eat is worth.
    effects: { health: 18, sanity: 6 },
    color: 0x8d9aa2,
    verb: 'Take',
    noEffect: 'Nothing about you needs it yet.',
  },
  candy: {
    kind: 'candy',
    label: 'Hard Candy',
    description: 'Individually wrapped, fruit flavoured. Sugar helps more than it should down here.',
    consumable: true,
    stackSize: 8,
    effects: { sanity: 8 },
    color: 0xd9566f,
    verb: 'Take',
    noEffect: 'There is nothing it can do for you.',
  },
  battery: {
    kind: 'battery',
    label: 'Battery',
    description: 'Heavier than it looks. Still holds a charge.',
    consumable: true,
    stackSize: 4,
    // Handled as a 'flashlight:recharge' event; the player controller owns charge.
    effects: {},
    color: 0xc8a33c,
    verb: 'Take',
    noEffect: 'The flashlight is already full.',
  },
  flashlight: {
    kind: 'flashlight',
    label: 'Flashlight',
    description: 'Rubberised grip, cracked lens. Someone kept it working for a long time.',
    consumable: false,
    stackSize: 1,
    effects: {},
    color: 0xffd66b,
    verb: 'Take',
    noEffect: 'It is already in your hand.',
    refusal: 'Press F for the flashlight.',
  },
  bandage: {
    kind: 'bandage',
    label: 'Bandage',
    description: 'Sterile gauze in a paper sleeve. Stops the bleeding, not the ache.',
    consumable: true,
    stackSize: 3,
    effects: { health: 38 },
    color: 0xe8e2d2,
    verb: 'Take',
    noEffect: 'You are not injured.',
  },
  pills: {
    kind: 'pills',
    label: 'Pills',
    description: 'A blister strip with no name on the foil, only a lot number. They quiet things down.',
    consumable: true,
    stackSize: 5,
    // They work, and they cost you something small, which is why they work.
    // Kept under the almond water on purpose: the prize has to stay the prize.
    effects: { sanity: 40, health: -3 },
    color: 0xd06fa0,
    verb: 'Take',
    noEffect: 'You are steady enough without them.',
  },
  note: {
    kind: 'note',
    label: 'Note',
    description: 'Handwriting. Someone stood here long enough to write it.',
    consumable: true,
    stackSize: 1,
    effects: { sanity: 8 },
    color: 0xe6ded0,
    verb: 'Read',
    noEffect: 'You have read it.',
  },
  key: {
    kind: 'key',
    label: 'Key',
    description: 'Brass, worn smooth, no tag. It opens one door somewhere in here.',
    consumable: false,
    stackSize: 4,
    effects: {},
    color: 0xc0a64e,
    verb: 'Take',
    noEffect: 'Nothing happens.',
    refusal: 'It is for a door, not for you.',
  },
};

const STAT_KEYS: readonly StatKey[] = ['health', 'sanity'];

/** Static definition for an item kind. */
export function itemDef(kind: ItemKind): ItemDefEx {
  return DEFS[kind];
}

/** Every kind in table order. Handy for the HUD and for debug listings. */
export const ITEM_KINDS: readonly ItemKind[] = Object.keys(DEFS) as ItemKind[];

/**
 * True when consuming this would actually move something. Negative effects are
 * ignored here on purpose: an item is refused when it can only take, never
 * because it also takes.
 */
export function hasUsefulEffect(def: ItemDef, stats: Readonly<SurvivalStats>): boolean {
  for (const key of STAT_KEYS) {
    const delta = def.effects[key];
    if (delta !== undefined && delta > 0 && stats[key] < 100) return true;
  }
  return false;
}
